/**
 * File processing and download utilities
 */

export { fileDownloadFromResponse, supportsStreaming } from './fileDownloadFromResponse';

/**
 * Converts bytes to human-readable size format
 */
export const bytesToSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Reads a file as a `data:` URL. Rejects when the browser can't read it, rather than never settling.
 */
export const readFileAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('The file was not read as a data URL'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read'));
    reader.readAsDataURL(file);
  });

/**
 * Creates and triggers download of CSV content as a file
 */
export const csvLink = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Type guard for IE 10+ compatibility
  if ('msSaveBlob' in navigator) {
    (navigator as Navigator & { msSaveBlob?: (blob: Blob, filename: string) => void }).msSaveBlob?.(blob, filename);
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Released once the download has started; until then the URL keeps the whole Blob alive.
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }
  }
};
